// Designed QR via `qr-code-styling` (kozakdenys/qr-code-styling).
// Renders an SVG QR with rounded dots, Meta-Blue corner squares, a centered
// WhatsApp glyph, and high error-correction so the center logo doesn't break
// scan reliability. Client-only — qr-code-styling depends on the DOM, so we
// instantiate it inside useEffect and append into a ref'd container.
import { useEffect, useRef } from 'react';
import QRCodeStyling from 'qr-code-styling';

// SVGO-optimized QR-center logo (precision=0, ~15 KB). Inlined as a data URL
// at build time so qr-code-styling doesn't fetch it at runtime — eliminates
// the double-fetch the library makes per QR mount and removes a network
// dependency from QR rendering. Original 195 KB high-fidelity logo lives at
// /public/logo-mark.svg for hero/nav use; this variant is purpose-built for
// the ~50 px center mark where integer coordinates are visually identical.
import logoSvgRaw from '../../assets/logo-mark-qr.svg?raw';

interface ClientOnlyQrCodeProps {
  value: string;
  size: number;
  /**
   * Background color baked into the QR SVG. Pass `'transparent'` when the QR
   * sits on a glass / translucent tile so the surface shows through. Default
   * `'#FFFFFF'` preserves the original solid-white module for any consumer
   * that doesn't opt in.
   */
  backgroundColor?: string;
}

const HMA_LOGO_URL = `data:image/svg+xml;utf8,${encodeURIComponent(logoSvgRaw)}`;

export function ClientOnlyQrCode({
  value,
  size,
  backgroundColor = '#FFFFFF',
}: ClientOnlyQrCodeProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.innerHTML = '';
    const qr = new QRCodeStyling({
      width: size,
      height: size,
      type: 'svg',
      data: value,
      margin: 0,
      qrOptions: { errorCorrectionLevel: 'H' },
      dotsOptions: { type: 'rounded', color: '#004BB9' },
      backgroundOptions: { color: backgroundColor },
      cornersSquareOptions: { type: 'extra-rounded', color: '#004BB9' },
      cornersDotOptions: { type: 'dot', color: '#004BB9' },
      image: HMA_LOGO_URL,
      imageOptions: { hideBackgroundDots: true, imageSize: 0.32, margin: 2 },
    });
    qr.append(el);
    return () => {
      el.innerHTML = '';
    };
  }, [value, size, backgroundColor]);

  return (
    <div
      ref={ref}
      data-testid="qr-canvas"
      style={{ display: 'block', width: size, height: size }}
    />
  );
}
